<template>
  <main class="l-main p-seed-keywords">
    <TitleMain
      icon="sitekeywordanalysis"
      faq="/sections/360001240055"
      howTo="/sections/4404631665295"
    >
      <span class="title">成果チェック</span>
      検索順位の推移やクリック率などの成果データを分析し、<br />改善に取り組むべきキーワードを見つけましょう。
    </TitleMain>
    <div class="l-container">
      <div class="l-toolBlock">
        <ButtonToProjects />
        <GaTypeStatus gaType="ga4" :project="project" />
        <Breadcrumb />
      </div>
      <div class="l-block">
        <div class="l-block__header">
          <TitleSub icon="sitekeywordanalysis" level="h2">
            <span class="title">検索順位チェック</span>
            <Calendar
              :range="period.range"
              :value="period.value"
              listType="gsc"
              @input="updatePeriod"
            />
          </TitleSub>
        </div>
        <Tab>
          <TabItem
            v-for="item in devices"
            :key="item.id"
            :id="item.id"
            :active="device"
            @click="updateKeywordDevice"
          >
            <Icon2 :variant="item.id" />
            {{ item.label }}
          </TabItem>
          <div class="c-tab__item c-tab__item--free">
            <ButtonDownloadKeywords
              :device="device"
              :from="period.value[0]"
              :project="project"
              :to="period.value[1]"
            >
              <span>CSVダウンロード</span>
            </ButtonDownloadKeywords>
          </div>
        </Tab>
        <div class="l-block__content">
          <Hint v-if="!isLoading && !hasKeyword">
            <template slot="title">
              Hint!
            </template>
            <p>
              まずはキーワードを追加し、検索順位を調査しましょう。
            </p>
            <template slot="btn">
              <Button
                size="w240"
                fsize="f16"
                variant="main"
                icon="arrow_r"
                @click="openModal"
              >
                キーワードを追加
              </Button>
            </template>
          </Hint>
          <div v-else class="p-seed-keywords-selected">
            <template v-if="hasKeyword">
              <h2 class="p-seed-keywords-selected__title">
                <span class="c-text--note">選択中のキーワード</span>
                {{ selectedKeyword }}
              </h2>
              <Button
                variant="border"
                fsize="f12"
                size="w120"
                @click="reset"
                v-if="selectedId"
              >
                <Icon variant="return_gn" :size="12" />
                選択を解除する
              </Button>
              <router-link
                :to="{
                  name: 'configProject',
                  params: { projectId: project.id },
                  hash: '#siteImportantKeyword'
                }"
                class="p-seed-keywords-selected__config"
              >
                <Icon variant="gear_gy" :size="14" />確認・変更
              </router-link>
              <Button
                variant="main"
                fsize="f16"
                icon="plus"
                class="l-w-190 l-pr-4"
                @click="openModal"
              >
                キーワードを追加
              </Button>
            </template>
          </div>
          <template v-if="selectedId">
            <MetricsKeyword
              :device="device"
              :keywordId="selectedId"
              :period="period.value"
              :project="project"
              :isLoading="isLoading"
              :selected="selected"
            >
            </MetricsKeyword>
          </template>
          <template v-if="hasKeyword">
            <div class="p-seed-keywords-searchform">
              <SearchForm
                :value="params.filter"
                size="sm"
                @change="updateFilter"
                @reset="resetFilter"
                placeholder="検索したいキーワードを入力"
              />
            </div>
            <div class="p-seed-keywords-nav">
              <Pager
                v-if="hasPages"
                :limit="params.limit"
                :offset="params.offset"
                :total="filteredItems.length"
                @change="updateOffset"
              />
              <ul class="c-graph-legend">
                <li class="c-graph-legend__item c-graph-legend__item--rank">
                  1位
                </li>
                <li class="c-graph-legend__item c-graph-legend__item--rank">
                  2位～5位
                </li>
                <li class="c-graph-legend__item c-graph-legend__item--rank">
                  6位～10位
                </li>
                <li class="c-graph-legend__item c-graph-legend__item--rank">
                  11位以下
                </li>
                <li class="c-graph-legend__item c-graph-legend__item--rank">
                  圏外
                </li>
                <li class="c-graph-legend__item is-nodata">
                  <Icon2 variant="attention" :size="14" />データ取得中
                </li>
                <li class="c-graph-legend__item is-nodata">
                  - GSC順位データなし
                </li>
                <li class="c-graph-legend__item is-nodata">
                  <img
                    class="c-graph-legend_icon"
                    src="@/assets/img/flag-task-other.svg"
                  />
                  タスク完了日
                </li>
              </ul>
            </div>
            <Keywords
              :items="sortedItems"
              :limitedItems="limitedItems"
              :period="period.value"
              :project="project"
              :selectedId="selectedId"
              :sort="params.sort"
              @select="selectItem"
              @sort="updateSort"
              @displayItem="displayItem"
            />
          </template>
          <Loading v-if="isLoading" variant="inline" />
        </div>
      </div>
    </div>
    <portal-target class="c-form-task__wrapper" name="form-add-tasks" />
    <ModalAddKeyword
      v-if="isOpenModal"
      :items="data.items"
      :keyword="keyword"
      :project="project"
      :isLoading="isLoading"
      @add="addKeywords"
      @close="closeModal"
    />
  </main>
</template>

<script>
import { CALENDAR } from '@/constants';
import Breadcrumb from '@/components/Breadcrumb';
import Button from '@/components/Button';
import ButtonToProjects from '@/components/ButtonToProjects';
import ButtonDownloadKeywords from '@/components/siteImportantKeywordGA4/ButtonDownloadKeywords';
import Calendar from '@/components/Calendar';
import GaTypeStatus from '@/components/GaTypeStatus';
import Hint from '@/components/Hint';
import Icon from '@/components/Icon';
import Icon2 from '@/components/Icon2';
import Keywords from '@/components/siteImportantKeywordGA4/Keywords';
import Loading from '@/components/Loading';
import MetricsKeyword from '@/components/siteImportantKeywordGA4/MetricsKeyword';
import ModalAddKeyword from '@/components/siteImportantKeywordGA4/ModalAddKeyword';
import Pager from '@/components/Pager';
import SearchForm from '@/components/SearchForm';
import Tab from '@/components/Tab';
import TabItem from '@/components/TabItem';
import TitleMain from '@/components/TitleMain';
import TitleSub from '@/components/TitleSub';
import utils from '@/utils';
import apiProjects from '@/api/projects';
import axios from 'axios';

export default {
  props: {
    id: {
      type: String,
      required: false
    },
    keyword: {
      type: String,
      required: false
    },
    project: {
      type: Object,
      required: true
    },
    status: {
      type: String,
      required: false
    }
  },
  components: {
    Breadcrumb,
    Button,
    ButtonToProjects,
    ButtonDownloadKeywords,
    Calendar,
    GaTypeStatus,
    Hint,
    Icon,
    Icon2,
    Keywords,
    Loading,
    MetricsKeyword,
    ModalAddKeyword,
    Pager,
    SearchForm,
    Tab,
    TabItem,
    TitleMain,
    TitleSub
  },
  data() {
    return {
      data: {
        items: []
      },
      devices: [
        {
          id: 'desktop',
          label: 'PC'
        },
        {
          id: 'mobile',
          label: 'スマホ'
        }
      ],
      device: 'desktop',
      listHeight: 0,
      params: {
        filter: '',
        offset: 0,
        limit: 100,
        sort: {
          name: 'taskDoneDate',
          orderby: 'desc',
          type: 'date'
        }
      },
      period: {
        range: [],
        value: []
      },
      request: {
        expiresAt: 10 * 60 * 1000, //10分
        timeoutDelay: 10 * 1000 //10秒
      },
      selectedId: '',
      isLoading: true,
      isOpenModal: this.status === 'add'
    };
  },
  computed: {
    filteredItems() {
      if (!this.params.filter) {
        return this.data.items;
      }
      const searchString = utils.convertToSearchString(this.params.filter);

      return this.data.items.filter(item => {
        // ひらがなとカタカナを区別しないよう、ひらがなに変換
        const convertedKeyword = utils.convertToSearchString(item.keyword);
        return convertedKeyword.indexOf(searchString) !== -1;
      });
    },
    limitedItems() {
      return this.sortedItems.slice(
        this.params.offset,
        this.params.offset + this.params.limit
      );
    },
    selected() {
      return this.data.items.find(item => item.id === this.selectedId);
    },
    sortedItems() {
      //sortするためにタスク完了日パラメータの階層を変更する
      let items = this.filteredItems.slice().map(item => {
        if (item.doneTask != null) {
          item.taskDoneDate = item.doneTask.typeDoneDate;
        }
        return item;
      });

      // デフォルトでキーワード昇順のソートをかける
      items = utils.sort(items, 'string', 'asc', 'keyword');

      return utils.sort(
        items,
        this.params.sort.type,
        this.params.sort.orderby,
        this.params.sort.name
      );
    },
    hasKeyword() {
      return this.data.items.length ? true : false;
    },
    hasPages() {
      return this.filteredItems.length > this.params.limit;
    },
    selectedKeyword() {
      return this.selected ? this.selected.keyword : '未選択';
    }
  },
  watch: {
    'data.items': {
      handler(value) {
        if (!this.selectedId) {
          // id（クエリパラメータ）がitemsの中に存在する場合は、そのitemを選択状態にする
          if (this.id && value.some(v => v.id === this.id)) {
            this.selectItem(this.id);
          }
        }
        this.setListHeight();
      }
    }
  },
  created() {
    // 環境がdemoの場合はデモ用の表示期間で固定するようにする
    if (process.env.VUE_APP_ENV === 'demo') {
      this.period.range = utils.getSiteImportantKeywordDemoPeriod('all');
      this.period.value = utils.getSiteImportantKeywordDemoPeriod();
      this.period.range[1] = utils.getDateResetHours(this.period.range[1]);
      this.period.value[1] = utils.getDateResetHours(this.period.value[1]);
    } else {
      this.period.range = utils.getSiteImportantKeywordPeriod('all');
      this.period.value = utils.getSiteImportantKeywordPeriod();
      this.period.range[1] = utils.getDateResetHours();
      this.period.value[1] = utils.getDateResetHours();
    }

    this.getKeywords();
    this.$store.dispatch('task/getTaskSolutions');
  },
  beforeDestroy() {
    this.cancelRequests();
  },
  methods: {
    getKeywords() {
      apiProjects.observationKeywords
        .get(this.auth.idToken, this.project.id)
        .then(response => {
          const items = response.data.items.map(item => {
            item.isLoading = true;
            return item;
          });
          // タスクリストの導線からキーワード登録して遷移してきた場合
          if (this.id && this.status === 'new') {
            for (let i = 0; i < items.length; i++) {
              if (items[i].id === this.id) {
                this.selectedId = this.id;
                // 登録直後なので赤色▲を表示しないようにする
                items[i].isNew = true;
                break;
              }
            }
          }
          this.data.items = items;
          this.isLoading = false;
        })
        .catch(error => {
          this.isLoading = false;
        });
    },
    getRank(id) {
      const index = this.data.items.findIndex(item => item.id === id);
      const item = Object.assign({}, this.data.items[index]);
      const params = {
        from: utils.formatDate(this.period.value[0]),
        to: utils.formatDate(this.period.value[1]),
        country: 'JPN',
        device: this.device
      };
      const cancelToken = axios.CancelToken.source();
      this.data.items[index].cancelToken = cancelToken;

      if (!this.data.items[index].expiresAt) {
        this.data.items[index].expiresAt = Date.now() + this.request.expiresAt;
      }

      return apiProjects.observationKeywords.detail
        .get(
          params,
          this.auth.idToken,
          this.project.id,
          id,
          cancelToken.token,
          index + 1
        )
        .then(response => {
          // itemsが空の場合はデータを再取得する
          if (!response.data.items.length) {
            return Promise.reject();
          }
          item.dates = response.data.items.filter(d => {
            const date = utils.getDateResetHours(d.date);
            // 指定期間範囲内のデータのみitemsに入れる
            // FakeAPIの場合、期間を1日のみ選択した時でも全データを返してくるため
            // FakeじゃないAPIの場合でも、直近3日間のデータはカットする
            if (
              date >= this.period.value[0] &&
              date <= this.period.value[1] &&
              date <= CALENDAR.gsc.maxDate
            ) {
              return true;
            }
          });
          item.dictionary = response.data.dictionary;
          item.isLoading = false;
          this.data.items.splice(index, 1, item);
          item.doneTasks = response.data.doneTasks;
        })
        .catch(error => {
          // 非同期処理をキャンセルした時もここを通るが、それ以外の時
          if (!axios.isCancel(error)) {
            // 初回リクエストから10分以内であれば再取得のタイマー設定
            if (Date.now() < this.data.items[index].expiresAt) {
              this.data.items[index].timeoutId = setTimeout(() => {
                this.getRank(id, index);
              }, this.request.timeoutDelay);
            } else {
              // 10分経っても取得できなかったら諦める
              item.isLoading = false;
              this.data.items.splice(index, 1, item);
            }
          }
        });
    },
    cancelRequests() {
      // タイマーと非同期通信を止める
      for (let i = 0; i < this.data.items.length; i++) {
        const item = this.data.items[i];
        if (item.timeoutId) {
          clearTimeout(item.timeoutId);
        }
        if (item.cancelToken) {
          item.cancelToken.cancel();
        }
      }
    },
    selectItem(id, index) {
      this.selectedId = id;
      // 選択されたキーワードは表示されてなくても順位を取得する
      this.getRank(this.selectedId);
      // 選択したキーワードがテーブルに表示されていない場合は表示する
      if (
        index < this.params.offset ||
        index >= this.params.offset + this.params.limit
      ) {
        // indexをlimitの倍数で丸める
        const offset =
          Math.floor(index / this.params.limit) * this.params.limit;
        this.params.offset = offset;
      }
    },
    resetRanks() {
      // 全キーワードの順位の情報を初期値に戻す
      this.data.items = this.data.items.map(item => {
        delete item.dates;
        delete item.dictionary;
        item.isLoading = true;
        return item;
      });
    },
    updatePeriod(value) {
      this.period.value = value;
      this.resetRanks();
      // リセット後、選択中のキーワードの順位を更新
      if (this.hasKeyword && this.selectedId != '') {
        this.getRank(this.selectedId);
      }
    },
    updateFilter(value) {
      this.params.filter = value;
      this.params.offset = 0;

      if (process.env.VUE_APP_ENV === 'demo') {
        return;
      }
      amplitude
        .getInstance()
        .logEvent('Search Keyword - Site Important Keyword');
    },
    resetFilter() {
      this.params.filter = '';
      this.params.offset = 0;
    },
    updateOffset(offset) {
      this.params.offset = offset;
    },
    updateKeywordDevice(id) {
      this.device = id;
      this.resetRanks();
      // リセット後、選択中のキーワードの順位を更新
      if (this.hasKeyword && this.selectedId != '') {
        this.getRank(this.selectedId);
      }
    },
    updateSort(sort) {
      this.params.offset = 0;
      this.params.sort = sort;
    },
    addKeywords(items) {
      this.closeModal();

      items = items.map(item => {
        item.isLoading = true;
        // 登録直後なので赤色▲を表示しないようにする
        item.isNew = true;
        return item;
      });
      this.data.items = this.data.items.concat(items);
    },
    setListHeight() {
      this.$nextTick(() => {
        if (!this.$refs.listItems) {
          return;
        }
        this.listHeight = this.$refs.listItems.clientHeight;
      });
    },
    openModal() {
      this.isOpenModal = true;
    },
    closeModal() {
      this.isOpenModal = false;
    },
    displayItem(id) {
      this.getRank(id);
    },
    reset() {
      this.selectedId = '';
      if (process.env.VUE_APP_ENV === 'demo') {
        return;
      }
      amplitude
        .getInstance()
        .logEvent('Unselect Keyword - Site Important Keyword');
    }
  }
};
</script>
